import { FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import flatMapDeep from 'lodash-es/flatMapDeep';

import { MenuItemChild } from '@da-g3-acl/shared/common';

import { MenuItemPath } from '../../../types/menuItemPath';
import { PinOutlineIcon } from '../../icons/PinOutlineIcon';
import { PinnedIcon } from '../../icons/PinnedIcon';

import MenuItem from './MenuItem';
import styles from './index.module.scss';

export interface StatusBarMenuProps {
	selectedMenuItemIds: string[] | undefined;
	menuItems: MenuItemChild[];
	showTags?: boolean;
	onItemSelected: (id: string | undefined) => void;
	onItemsCleared?: () => void;
	defaultTitle?: string;
	selectedAsTitle?: boolean;
	maxHeight?: number | undefined;
	className?: string;
	tooltip?: string;
	allowPin?: boolean;
	children?: ReactNode;
}

type MenuItemChildWithPath = Omit<MenuItemChild, 'children'> & {
	path: MenuItemPath;
	children: MenuItemChildWithPath[] | undefined;
};

type MenuItemChildWithPathAndSiblingPosition = MenuItemChildWithPath & { idx: number };
const getMenuItemChildWithPath = (
	parentPath: MenuItemPath | undefined,
	child: MenuItemChild,
): MenuItemChildWithPath => {
	const path: MenuItemPath = parentPath ? ([...parentPath, child.id] as MenuItemPath) : [child.id];

	return {
		...child,
		path: path,
		children: child.children?.map((c) => getMenuItemChildWithPath(path, c)),
	};
};

/**
 * Handles a menu of items. Internally, steps have been taken to have many levels, but will only handle 2 levels until required.
 */
export const StatusBarMenu: FC<StatusBarMenuProps> = ({
	selectedMenuItemIds,
	menuItems,
	showTags,
	onItemSelected,
	onItemsCleared,
	defaultTitle,
	selectedAsTitle,
	maxHeight,
	className,
	tooltip,
	allowPin = true,
	children,
}: StatusBarMenuProps) => {
	const [showMenu, setShowMenu] = useState<boolean>(false);
	const [expandedPath, setExpandedPath] = useState<MenuItemPath>();
	const [selectedMenuItemPaths, setSelectedMenuItemPaths] = useState<MenuItemPath[]>([]);
	const menuButtonRef = useRef<HTMLDivElement>(null);
	const [isPinned, setIsPinned] = useState(false);

	/**
	 * Memo (from a structure) of menu items with paths included as a part of that structure
	 */
	const menuItemsInWithPathsBySection = useMemo(
		() =>
			menuItems
				.toSorted((a, b) => (a.sectionName + a.name).localeCompare(b.sectionName + b.name))
				.reduce<Record<string, MenuItemChildWithPath[]>>(
					(agg, next) => ({
						...agg,
						[next.sectionName]: [
							...(agg[next.sectionName] ?? []),
							getMenuItemChildWithPath(undefined, next),
						],
					}),
					{},
				),
		[menuItems],
	);

	/**
	 * Memo of the current selection path as a series of items, with the position of it in the list of its siblings.
	 * This is used for rendering the list structure
	 */
	const pathAsItemsWithIndex = useMemo<MenuItemChildWithPathAndSiblingPosition[] | undefined>(() => {
		if (!expandedPath?.length) return undefined;

		return expandedPath.reduce<MenuItemChildWithPathAndSiblingPosition[]>((agg, _, pathLevel) => {
			const currentLevelMenuItems = pathLevel
				? (agg[pathLevel - 1].children ?? [])
				: Object.keys(menuItemsInWithPathsBySection).reduce<MenuItemChildWithPath[]>(
						(agg, key) => [...agg, ...menuItemsInWithPathsBySection[key]],
						[],
					);
			const currentLevelItemIndex = currentLevelMenuItems.findIndex((i) =>
				pathsEqual(i.path, expandedPath, false),
			);
			if (currentLevelItemIndex === -1) {
				return agg; // Expanded menu item may not be found (temporarily) when switching to a new list of items
			}
			return [...agg, { ...currentLevelMenuItems[currentLevelItemIndex], idx: currentLevelItemIndex }];
		}, []);
	}, [expandedPath, menuItemsInWithPathsBySection]);

	/**
	 * An effect for updating the selected item paths via the selected id input prop.
	 * Closes the menu if no items are selected
	 */
	useEffect(() => {
		if (!selectedMenuItemIds?.length) {
			setSelectedMenuItemPaths([]);
			setExpandedPath(undefined);
		} else {
			const flatItems = flatMapDeep(
				Object.keys(menuItemsInWithPathsBySection).reduce<MenuItemChildWithPath[]>(
					(agg, key) => [...agg, ...menuItemsInWithPathsBySection[key]],
					[],
				),
				(item) => [item, ...(item.children ?? [])],
			);
			setSelectedMenuItemPaths(flatItems.filter((i) => selectedMenuItemIds?.includes(i.id)).map((i) => i.path));
		}
	}, [selectedMenuItemIds, menuItemsInWithPathsBySection]);

	/**
	 * Hook for managing closing of the menu when clicked away
	 */
	useEffect(() => {
		if (!showMenu) return;
		const closeMenu = (event: MouseEvent): void => {
			const menuButton = menuButtonRef.current;
			const nearestMenuButton = (event.target as HTMLElement).closest(`.${styles.statusBarMenu}`);
			if (nearestMenuButton === menuButton) return;
			setShowMenu(false);
			setExpandedPath(undefined);
		};
		addEventListener('mousedown', closeMenu);

		return (): void => {
			removeEventListener('mousedown', closeMenu);
		};
	}, [showMenu, setShowMenu, menuItems, selectedMenuItemPaths, isPinned]);

	const canSelectItem = !!onItemSelected;

	return (
		<div
			className={clsx(styles.statusBarMenu, className, {
				[styles.selectable]: canSelectItem,
			})}
			onClick={() => setShowMenu((i) => canSelectItem && !i)}
			title={tooltip}
			ref={menuButtonRef}
		>
			{children}
			{!children && (
				<>
					<div className={styles.statusBarMenuTitle}>
						{(selectedAsTitle && selectedMenuItemPaths.length < 2) ??
							defaultTitle ??
							'Select...' +
								(selectedMenuItemPaths.length > 1 ? ` (${selectedMenuItemPaths.length})` : '')}
					</div>
				</>
			)}

			{canSelectItem && (
				<div className={styles.menuContentContainer} style={{ maxHeight: maxHeight }}>
					<ul
						className={clsx(styles.firstLevel, {
							[styles.show]: showMenu,
						})}
					>
						{allowPin && (
							<li className={styles.pinHeader} onClick={(e) => e.stopPropagation()}>
								{onItemsCleared && !!selectedMenuItemIds?.length && (
									<div
										className={styles.clearContainer}
										onClick={(e) => {
											onItemsCleared();
											e.stopPropagation();
										}}
									>
										<span>Deselect</span>
									</div>
								)}
								<div
									className={clsx(styles.pinContainer, { [styles.pinned]: isPinned })}
									onClick={(e) => {
										setIsPinned((p) => !p);
										e.stopPropagation();
									}}
								>
									<span className={styles.pinIcon} title="Pin Menu">
										{isPinned ? <PinnedIcon size={16} /> : <PinOutlineIcon size={16} />}
									</span>
								</div>
							</li>
						)}
						{Object.keys(menuItemsInWithPathsBySection)
							.toSorted()
							.map((key) =>
								menuItemsInWithPathsBySection[key].map((item, idx) => (
									<Fragment key={`${key}-${item.id}-${idx}`}>
										{idx === 0 && <li className={styles.section}>{key}</li>}
										<MenuItem
											key={`menu-item-1-${idx}`}
											item={item}
											isParentOfSelected={
												!!selectedMenuItemPaths.find((p) => pathsEqual(item.path, p, true)) &&
												!!item.children?.length
											}
											menuPath={item.path}
											selectedMenuPaths={selectedMenuItemPaths}
											showTag={!!showTags}
											onClick={(e) => {
												const hasChild = item.children && item.children.length > 0;
												e.stopPropagation();
												// If the item has a child
												if (hasChild) {
													// And the item is already expanded
													if (expandedPath && pathsEqual(expandedPath, item.path))
														// Then close the path (drop the last element, closing the current end leaf)
														setExpandedPath((value) => {
															if (!value) return value;
															const newValue = [...value];
															newValue.pop();
															return newValue as MenuItemPath;
														});
													// Otherwise, lets show the leaf!
													else setExpandedPath(item.path);
													// Otherwise, it is not a menu, and we should select it
												} else {
													setExpandedPath(undefined);
													// If the menu is pinned, then don't close
													setShowMenu(isPinned);
													onItemSelected && onItemSelected(item.id);
												}
											}}
										/>
									</Fragment>
								)),
							)}
					</ul>
					<ul
						className={clsx(styles.secondLevel, {
							[styles.show]: !!expandedPath && showMenu,
						})}
					>
						{pathAsItemsWithIndex &&
							pathAsItemsWithIndex[0]?.children?.map((item, idx) => (
								<Fragment key={`${pathAsItemsWithIndex[0].idx}-${item.id}-${idx}`}>
									{idx === 0 && <li className={styles.section}>{item.sectionName}</li>}
									<MenuItem
										key={`menu-item-1-${idx}`}
										item={item}
										isParentOfSelected={false}
										menuPath={item.path}
										selectedMenuPaths={selectedMenuItemPaths}
										showTag={!!showTags}
										onClick={(e) => {
											e.stopPropagation();
											setShowMenu(isPinned);
											onItemSelected && onItemSelected(item.id);
										}}
									/>
								</Fragment>
							))}
						{/** Pad the secondary menu so that it aligns to the currently selected primary menu item */}
						{pathAsItemsWithIndex &&
							pathAsItemsWithIndex?.length &&
							[
								...Array(
									Math.max(
										menuItems.length -
											pathAsItemsWithIndex[0].idx -
											(pathAsItemsWithIndex[0].children?.length ?? 0),
										0,
									),
								),
							].map((_, index) => (
								<li key={`dummy-${index}`} className={styles.dummySecondaryMenu}>
									&nbsp;
								</li>
							))}
					</ul>
				</div>
			)}
		</div>
	);
};

/**
 * Check if two paths are equal
 * @param {MenuItemPath} a First Path
 * @param {MenuItemPath} b Second path
 * @param {boolean} matchIfDescendant If one path is wholly contained within another (i.e: it is a parent/child) then also match.
 */
const pathsEqual = (a: MenuItemPath, b: MenuItemPath, matchIfDescendant?: boolean): boolean => {
	// If exact match, then return true
	if (JSON.stringify(a) === JSON.stringify(b)) return true;
	// If we aren't looking for siblings, then matching is complete
	if (!matchIfDescendant) return false;
	// If A is the same as the start of b, then it is a parent
	// i.e: [A, B, C] === [A, B, C, D]
	if (JSON.stringify(a) === JSON.stringify(b.slice(0, a.length))) return true;
	// If B is the same as the start of a, then it is a parent
	if (JSON.stringify(a.slice(b.length)) === JSON.stringify(b)) return true;
	// Otherwise, the paths differ
	return false;
};
